import moment from 'moment';
import { i18n, tokens, Services } from "@simplybenefits/sb-ui-library";

const now = new Date();
const firstOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);

/**
 * Generates advisor reports data with various sections and filters.
 * @returns {Object} Object containing advisor reports data.
 */
const advisorReportsData = () => ({
    [i18n.t(tokens.labels.reports.membersAndUsage)]: {
        [i18n.t(tokens.labels.reports.overAgeMemberListing)]: {
            title: i18n.t(tokens.labels.reports.overAgeMemberAnniversary),
            icon: 'elderly',
            description: i18n.t(tokens.labels.reports.viewAllMembersWhoWillBecomeOlder),
            filters: {
                dateRange: {
                    type: 'date',
                    startDate: moment(),
                    endDate: moment().add(6, 'month')
                },
                age: {
                    type: 'number',
                    label: i18n.t(tokens.labels.reports.age),
                    isRequired: true,
                    value: 65
                },
                division: {
                    type: 'select',
                    label: i18n.t(tokens.titles.division),
                    options: [ i18n.t(tokens.labels.reports.allDivisions) ],
                    value: i18n.t(tokens.labels.reports.allDivisions)
                },
                status: {
                    type: 'select',
                    label: i18n.t(tokens.labels.employees.status),
                    options: Services.selectFieldOptions.memberStatusOptions(),
                    value: i18n.t(tokens.labels.reports.all)
                }
            }
        },
        [i18n.t(tokens.labels.reports.overAgeDependentListing)]: {
            title: i18n.t(tokens.labels.reports.overageDependent),
            icon: 'cake',
            description: i18n.t(tokens.labels.reports.viewAllCurrentDependentsOverage),
            rules: [
                {
                    type: 'account',
                    excludeIfTrue: 'isPrivateLabel'
                },
                {
                    type: 'account',
                    excludeIfTrue: 'isMarketplaceOnly'
                }
            ],
            testid: 'over-age-dependents',
            titleExpansionKeys: [ 'groupNum', 'company', 'division' ],
            filters: {
                division: {
                    type: 'select',
                    label: i18n.t(tokens.titles.division),
                    options: [ i18n.t(tokens.labels.reports.allDivisions) ],
                    value: i18n.t(tokens.labels.reports.allDivisions)
                }
            }
        },
        [i18n.t(tokens.labels.reports.dependentListing)]: {
            icon: 'groups',
            description: i18n.t(tokens.labels.reports.viewAllCurrentDependents),
            testid: 'dependent-listing',
            titleExpansionKeys: [ 'groupNum', 'company', 'division' ],
            filters: {
                division: {
                    type: 'select',
                    label: i18n.t(tokens.titles.division),
                    options: [ i18n.t(tokens.labels.reports.allDivisions) ],
                    value: i18n.t(tokens.labels.reports.allDivisions)
                },
                status: {
                    type: 'select',
                    label: i18n.t(tokens.labels.employees.status),
                    options: [
                        i18n.t(tokens.labels.reports.all),
                        i18n.t(tokens.labels.employees.active),
                        i18n.t(tokens.labels.employees.accountDisabled)
                    ],
                    value: i18n.t(tokens.labels.reports.all)
                }
            }
        },
        [i18n.t(tokens.labels.reports.spendingAccountUsages)]: {
            title: i18n.t(tokens.labels.reports.hsaLsaUsages),
            icon: 'receipt',
            description: i18n.t(tokens.labels.reports.viewSpendingAccountPremiums),
            rules: [
                {
                    type: 'account',
                    excludeIfTrue: 'isPrivateLabel'
                },
                {
                    type: 'account',
                    excludeIfTrue: 'isMarketplaceOnly'
                }
            ],
            titleExpansionKeys: [ 'groupNum', 'company', 'division', 'class' ],
            filters: {
                dateRange: {
                    type: 'date',
                    startDate: moment().startOf('year'),
                    endDate: moment()
                },
                class: {
                    type: 'select',
                    label: i18n.t(tokens.titles.class),
                    options: [ i18n.t(tokens.labels.reports.allClasses) ],
                    value: i18n.t(tokens.labels.reports.allClasses)
                },
                division: {
                    type: 'select',
                    label: i18n.t(tokens.titles.division),
                    options: [ i18n.t(tokens.labels.reports.allDivisions) ],
                    value: i18n.t(tokens.labels.reports.allDivisions)
                },
                member: true,
                type: {
                    type: 'select',
                    label: i18n.t(tokens.labels.coverage.benefit),
                    options: Services.selectFieldOptions.spendingAccountOptions(),
                    value: i18n.t(tokens.labels.reports.all)
                }
            }
        },
        [i18n.t(tokens.labels.reports.hsaBalance)]: {
            icon: 'healthAndSafety',
            description: i18n.t(tokens.labels.reports.viewCurrentHsaBalancesPerMember),
            rules: [
                {
                    type: 'account',
                    excludeIfTrue: 'isPrivateLabel'
                },
                {
                    type: 'account',
                    excludeIfTrue: 'isMarketplaceOnly'
                }
            ],
            testid: 'hsa-balance',
            titleExpansionKeys: [ 'groupNum', 'company', 'division', 'class' ],
            filters: {
                class: {
                    type: 'select',
                    label: i18n.t(tokens.titles.class),
                    options: [ i18n.t(tokens.labels.reports.allClasses) ],
                    value: i18n.t(tokens.labels.reports.allClasses)
                },
                division: {
                    type: 'select',
                    label: i18n.t(tokens.titles.division),
                    options: [ i18n.t(tokens.labels.reports.allDivisions) ],
                    value: i18n.t(tokens.labels.reports.allDivisions)
                },
                status: {
                    type: 'select',
                    label: i18n.t(tokens.labels.employees.status),
                    options: Services.selectFieldOptions.memberStatusOptions(),
                    value: i18n.t(tokens.labels.reports.all)
                }
            }
        },
        [i18n.t(tokens.labels.reports.lsaBalance)]: {
            icon: 'shoppingCart',
            description: i18n.t(tokens.labels.reports.viewCurrentLsaBalancesPerMember),
            rules: [
                {
                    type: 'account',
                    excludeIfTrue: 'isPrivateLabel'
                },
                {
                    type: 'account',
                    excludeIfTrue: 'isMarketplaceOnly'
                }
            ],
            titleExpansionKeys: [ 'groupNum', 'company', 'division', 'class' ],
            filters: {
                class: {
                    type: 'select',
                    label: i18n.t(tokens.titles.class),
                    options: [ i18n.t(tokens.labels.reports.allClasses) ],
                    value: i18n.t(tokens.labels.reports.allClasses)
                },
                division: {
                    type: 'select',
                    label: i18n.t(tokens.titles.division),
                    options: [ i18n.t(tokens.labels.reports.allDivisions) ],
                    value: i18n.t(tokens.labels.reports.allDivisions)
                },
                status: {
                    type: 'select',
                    label: i18n.t(tokens.labels.employees.status),
                    options: Services.selectFieldOptions.memberStatusOptions(),
                    value: i18n.t(tokens.labels.reports.all)
                }
            }
        },
        [i18n.t(tokens.labels.reports.certificateListing)]: {
            icon: 'assignment',
            description: i18n.t(tokens.labels.reports.getDataOnEachMember),
            testid: 'certificate-listing',
            titleExpansionKeys: [ 'groupNum', 'company', 'division' ],
            filters: {
                division: {
                    type: 'select',
                    label: i18n.t(tokens.titles.division),
                    options: [ i18n.t(tokens.labels.reports.allDivisions) ],
                    value: i18n.t(tokens.labels.reports.allDivisions)
                },
                status: {
                    type: 'select',
                    label: i18n.t(tokens.labels.employees.status),
                    options: Services.selectFieldOptions.memberStatusOptions(),
                    value: i18n.t(tokens.labels.reports.all)
                }
            }
        },
        [i18n.t(tokens.labels.reports.nemEligibilityReportTitle)]: {
            title: i18n.t(tokens.labels.reports.nemEligibilityReportTitle),
            icon: 'grading',
            description: i18n.t(tokens.labels.reports.nemEligibilityReportDesc),
            rules: [
                {
                    type: 'account',
                    excludeIfTrue: 'isPrivateLabel'
                },
                {
                    type: 'account',
                    excludeIfTrue: 'isMarketplaceOnly'
                }
            ],
            testid: 'nem-eligibility-report',
            filters: {
                class: {
                    type: 'select',
                    label: i18n.t(tokens.titles.class),
                    options: [ i18n.t(tokens.labels.reports.allClasses) ],
                    value: i18n.t(tokens.labels.reports.allClasses),
                    isRequired: true
                },
                division: {
                    type: 'select',
                    label: i18n.t(tokens.titles.division),
                    options: [ i18n.t(tokens.labels.reports.allDivisions) ],
                    value: i18n.t(tokens.labels.reports.allDivisions),
                    isRequired: true
                },
                benefit: {
                    type: 'select',
                    label: i18n.t(tokens.titles.benefit),
                    options: Services.selectFieldOptions.nemBenefitOptions(),
                    value: i18n.t(tokens.labels.reports.all),
                    isRequired: true
                },
                status: {
                    type: 'select',
                    label: i18n.t(tokens.titles.status),
                    options: Services.selectFieldOptions.memberStatusOptions(),
                    value: i18n.t(tokens.labels.reports.all),
                    isRequired: true
                }
            }
        },
        [i18n.t(tokens.labels.reports.paidClaimSummary)]: {
            icon: 'barCharts',
            rules: [
                {
                    type: 'users',
                    min: 10
                },
                {
                    type: 'account',
                    excludeIfTrue: 'isPrivateLabel'
                },
                {
                    type: 'account',
                    excludeIfTrue: 'isMarketplaceOnly'
                }
            ],
            description: i18n.t(tokens.labels.reports.paidClaimSummaryDesc),
            filters: {
                dateRange: {
                    type: 'date',
                    startDate: moment().startOf('year'),
                    endDate: moment(),
                    isRequired: true
                },
                dateRangeType: {
                    type: 'bubble-select',
                    label: i18n.t(tokens.labels.reports.dateRangeFilterType),
                    options: [ i18n.t(tokens.labels.reports.serviceDate), i18n.t(tokens.labels.reports.paidDate) ],
                    value: i18n.t(tokens.labels.reports.serviceDate),
                    isRequired: true
                },
                benefit: {
                    type: 'select',
                    label: i18n.t(tokens.labels.coverage.benefit),
                    options: [
                        i18n.t(tokens.labels.reports.all),
                        ...Services.selectFieldOptions.categoryOptions()
                    ],
                    value: i18n.t(tokens.labels.reports.all),
                    isRequired: true
                },
                class: {
                    type: 'select',
                    label: i18n.t(tokens.titles.class),
                    options: [ i18n.t(tokens.labels.reports.allClasses) ],
                    value: i18n.t(tokens.labels.reports.allClasses)
                },
                division: {
                    type: 'select',
                    label: i18n.t(tokens.titles.division),
                    options: [ i18n.t(tokens.labels.reports.allDivisions) ],
                    value: i18n.t(tokens.labels.reports.allDivisions)
                }
            }
        },
        [i18n.t(tokens.labels.reports.topDentalProceduresReportTitle)]: {
            icon: 'tagFaces',
            rules: [
                {
                    type: 'users',
                    min: 10 
                },
                {
                    type: 'account',
                    excludeIfTrue: 'isPrivateLabel'
                },
                {
                    type: 'account',
                    excludeIfTrue: 'isMarketplaceOnly'
                }
            ],
            description: i18n.t(tokens.labels.reports.topDentalProceduresReportDesc),
            titleExpansionKeys: {
                dateRange: {},
                class: { default: 'All Classes' },
                division: { default: 'All Divisions' },
                limit: { default: 'Top 10' }
            },
            filters: {
                dateRange: {
                    type: 'date',
                    startDate: moment().startOf('year'),
                    endDate: moment(),
                    isRequired: true
                },
                class: {
                    type: 'select',
                    label: i18n.t(tokens.titles.class),
                    options: [ i18n.t(tokens.labels.reports.allClasses) ],
                    value: i18n.t(tokens.labels.reports.allClasses)
                },
                division: {
                    type: 'select',
                    label: i18n.t(tokens.titles.division),
                    options: [ i18n.t(tokens.labels.reports.allDivisions) ],
                    value: i18n.t(tokens.labels.reports.allDivisions)
                },
                limit: {
                    type: 'select',
                    label: i18n.t(tokens.labels.reports.limit),
                    options: [
                        'Top 10', 'Top 20'
                    ],
                    value: 'Top 10',
                    isRequired: true
                }
            }
        }
    },
    [i18n.t(tokens.labels.reports.financial)]: {
        [i18n.t(tokens.labels.reports.asoMonthlySummary)]: {
            icon: 'adminPanelSettings',
            description: i18n.t(tokens.labels.reports.viewAsoMonthlySummary),
            rules: [
                {
                    type: 'account',
                    excludeIfTrue: 'isPrivateLabel'
                },
                {
                    type: 'account',
                    excludeIfTrue: 'isMarketplaceOnly'
                }
            ],
            titleExpansionKeys: [ 'groupNum', 'company', 'division' ],
            filters: {
                from: {
                    type: 'month',
                    value: firstOfMonth
                },
                to: {
                    type: 'month',
                    value: firstOfMonth
                },
                division: {
                    type: 'select',
                    label: i18n.t(tokens.titles.division),
                    options: [ i18n.t(tokens.labels.reports.allDivisions) ],
                    value: i18n.t(tokens.labels.reports.allDivisions)
                },
                benefit: {
                    type: 'select',
                    label: i18n.t(tokens.labels.coverage.benefit),
                    options: Services.selectFieldOptions.healthDentalOptions(),
                    value: i18n.t(tokens.dropdowns.healthDentalOptions.both)
                }
            }
        },
        [i18n.t(tokens.labels.reports.taxableBenefit)]: {
            icon: 'monetizationOn',
            description: i18n.t(tokens.labels.reports.viewYearlyPremium),
            rules: [
                {
                    type: 'account',
                    excludeIfTrue: 'isPrivateLabel'
                },
                {
                    type: 'account',
                    excludeIfTrue: 'isMarketplaceOnly'
                }
            ],
            testid: 'taxable-benefit',
            titleExpansionKeys: [ 'groupNum', 'company', 'division' ],
            filters: {
                dateRange: {
                    type: 'date',
                    startDate: moment().startOf('year'),
                    endDate: moment()
                },
                division: {
                    type: 'select',
                    label: i18n.t(tokens.titles.division),
                    options: [ i18n.t(tokens.labels.reports.allDivisions) ],
                    value: i18n.t(tokens.labels.reports.allDivisions)
                },
                // Members select with member options
                status: {
                    type: 'select',
                    label: i18n.t(tokens.labels.employees.status),
                    options: Services.selectFieldOptions.memberStatusOptions(),
                    value: i18n.t(tokens.labels.reports.all)
                }
            }
        },
        [i18n.t(tokens.labels.reports.lossRatio)]: {
            icon: 'compareArrows',
            description: i18n.t(tokens.labels.reports.viewLossRatio),
            rules: [
                {
                    type: 'account',
                    excludeIfTrue: 'isPrivateLabel'
                },
                {
                    type: 'account',
                    excludeIfTrue: 'isMarketplaceOnly'
                }
            ],
            testid: 'loss-ratio',
            filters: {
                dateRange: {
                    type: 'date',
                    startDate: moment().startOf('year'),
                    endDate: moment()
                },
                division: {
                    type: 'select',
                    label: i18n.t(tokens.titles.division),
                    options: [ i18n.t(tokens.labels.reports.allDivisions) ],
                    value: i18n.t(tokens.labels.reports.allDivisions)
                }
            }
        },
        [i18n.t(tokens.labels.reports.ltdReport)]: {
            title: i18n.t(tokens.labels.reports.ltd),
            icon: 'dateRange',
            description: i18n.t(tokens.labels.reports.viewLtdDisabilityReport),
            rules: [
                {
                    type: 'account',
                    excludeIfTrue: 'isPrivateLabel'
                },
                {
                    type: 'account',
                    excludeIfTrue: 'isMarketplaceOnly'
                }
            ],
            testid: 'ltd-report',
            filters: {
                division: {
                    type: 'select',
                    label: i18n.t(tokens.titles.division),
                    options: [ i18n.t(tokens.labels.reports.allDivisions) ],
                    value: i18n.t(tokens.labels.reports.allDivisions)
                },
                class: {
                    type: 'select',
                    label: i18n.t(tokens.titles.class),
                    options: [ i18n.t(tokens.labels.reports.allClasses) ],
                    value: i18n.t(tokens.labels.reports.allClasses)
                }
            }
        }
    },
    [i18n.t(tokens.labels.reports.renewal)]: {
        [i18n.t(tokens.labels.reports.topDrug)]: {
            icon: 'medication',
            titleExpansionKeys: [ 'groupNum', 'class' ],
            description: i18n.t(tokens.labels.reports.topDrugReportDesc),
            rules: [
                {
                    type: 'users',
                    min: 10
                },
                {
                    type: 'account',
                    excludeIfTrue: 'isPrivateLabel'
                },
                {
                    type: 'account',
                    excludeIfTrue: 'isMarketplaceOnly'
                }
            ],
            filters: {
                dateRange: {
                    type: 'date',
                    startDate: moment().startOf('year'),
                    endDate: moment(),
                    isRequired: true
                },
                class: {
                    type: 'select',
                    label: i18n.t(tokens.titles.class),
                    options: [ i18n.t(tokens.labels.reports.allClasses) ],
                    value: i18n.t(tokens.labels.reports.allClasses)
                },
                limit: {
                    type: 'select',
                    label: i18n.t(tokens.labels.reports.limit),
                    options: Services.selectFieldOptions.topDrugLimitOptions(),
                    value: i18n.t(tokens.labels.drugs.top10),
                    isRequired: true
                }
            }
        }
    }
});

export default advisorReportsData;